import { AlternativeCurrency } from './reservation/reservation';
import { FeeTypes } from './models/fee-types';

export interface CarType {
  id?: number;
  code?: string;
  name: string;
  order: number;
}

export interface CarCategory {
  id?: number;
  code?: string;
  name: string;
  order: number;
}

type CarCategoriesIndex =
  'Mini' | 'MiniElite' | 'Intermediate' | 'IntermediateElite' |
  'Economy' | 'EconomyElite' | 'Compact' | 'CompactElite' |
  'Intermediate' | 'IntermediateElite' | 'Standard' | 'StandardElite' |
  'Fullsize' | 'FullsizeElite' | 'Premium' | 'PremiumElite' |
  'Luxury' | 'LuxuryElite' | 'Oversize' | 'Special';

type CarTypesIndex =
  'TwoThreeDoors' | 'TwoOrFourDoors' | 'FourFiveDoors' | 'WagorOrEstate' |
  'PassengerVan' | 'Limousine' | 'Sport' | 'Convertible' |
  'Suv' | 'OpenAirAllTerrain' | 'Special' | 'PickUpRegularCar' |
  'PickUpExtendedCar' | 'SpecialOfferCar' | 'Coupe' | 'Monospace' |
  'RecreationalVehicle' | 'MotorHome' | 'TwoWheelVehicle' | 'Roadster' |
  'Crossover' | 'CommercialVan';

export type CarCategories = { [key in CarCategoriesIndex]: CarCategory };

export type CarTypes = { [key in CarTypesIndex]: CarType };

export interface AcrisCodes {
  categories: CarCategories;
  types: CarTypes;
}

export interface NumberSeats {
  min: number;
  max: number;
}

export interface NumberLargeBags {
  min: number;
  max: number;
}

export interface NumberDoors {
  min: number;
  max: number;
}

export interface Price {
  amount: number;
  currencyCode: string;
}

export interface Markdown {
  percentage: number;
  dollar: number;
}

export interface DynamicMarkdown {
  code: string;
  minMargin: number;
  markdown: Markdown;
  applied: boolean;
  appliedMarkdownType: number;
  amount: Price;
  priceOut: Price;
}

export interface DiscountValues {
  appliedDiscountType: number;
  percentage: number;
  dollar: number;
  applied: boolean;
  margin: number;
  minMargin: number;
  amount: number;
  priceOut: number;
}

export interface DBCValues {
  appliedDiscountType: number;
  percentage: number;
  dollar: number;
  applied: boolean;
  margin: number;
  minMargin: number;
  amount: number;
  priceOut: number;
}

export interface Address {
  cityName: string;
  countryCode: string;
  postalCode: string;
  stateCode: string;
  addressLine: string;
  isEmpty: boolean;
}

export interface Location {
  extendedLocationCode?: string;
  locationCode: string;
  locationName: string;
  address: Address;
  longitude: number;
  latitude: number;
  distance: number;
  unitOfMeasure: string;
  shuttleInfo: number;
}

export interface MileageRate {
  mileageType: string;
  totalAllowance: number;
  extraMileageCharge: number;
  unitOfMeasure: string;
  currencyCode: string;
  rateType: MileageRateType;
  allowance: number;
}

export enum MileageRateType {
  Unknown,
  Daily,
  Weekly,
  Monthly
}

export interface CarDetail {
  rateKey: string;
  netRateKey: string;
  rateCode: string;
  ratePlan: string;
  imageUrl: string;
  companyLogoUrl: string;
  companyName: string;
  carModelName: string;
  vehicleType: string;
  carCategory: string;
  carType: string;
  numberSeats: NumberSeats;
  numberLargeBags: NumberLargeBags;
  numberDoors: NumberDoors;
  dayRate: Price;
  totalRate: Price;
  totalRateNet: Price;
  totalRateMarket: Price;
  taxesAndFees: Price;
  dynamicMarkdown: DynamicMarkdown;
  isUnlimitedMileage: boolean;
  isAutomaticTransmission: boolean;
  isFreeCancellation: boolean;
  isAirConditioning: boolean;
  isAirBags: boolean;
  vendorCode: string;
  numberDays: number;
  numberHours: number;
  pickUpLocation: Location;
  returnLocation: Location;
  mileage: MileageRate;
  totalWithFees: Price;
  paymentOptions: PaymentOption[];
  pointsSubType: string;
  benefits: Benefits[];
  outsideAgentId: string;
  discountValues: DiscountValues;
  dbcValues: DBCValues;
}

export interface CarRateCodeFee {
  type: FeeTypes;
  fee: Price;
  description: string;
  isRefundable?: boolean;
  isOverridable?: boolean;
}

export interface VehicleCharge {
  vendorCode: string;
  rate: Price;
  mileageRate: MileageRate;
  subTotal: Price;
  totalCharge: Price;
  discount: DiscountModel;
}

export interface DiscountModel {
  amount: Price;
  discountPercentage: number;
}

export interface RefundSummary {
  card: Price[];
  alternativeCurrencies: AlternativeCurrency[];
  total: Price[];
}

export class InsuranceCoverage {
  purchaseInsurance: boolean;
  price: number;
}

export interface CheckPriceData {
  bookingKey: string;
  locationCode: string;
  locationName: string;
  locationOwner: string;
  operationTimeStart: string;
  operationTimeEnd: string;
  bookingMaxPeriod: number;
  bookingMinDays: number;
  bookingNumHours: number;
  bookingOvernightReq: string;
  approximateTotalPriceDetails?: any;
  commissionAmount?: any;
  commissionPercent?: any;
  vehicleType: string;
  totalDays: number;
  dailyPrices: Price[];
  carRateCodeFees: CarRateCodeFee[];
  totalFees: Price[];
  vehicleCharges: VehicleCharge[];
  bookingFees: CarRateCodeFee[];
  alternativeCurrencies: AlternativeCurrency[];
  alternativeCurrenciesFees: CarRateCodeFee[];
  total: Price[];
  refundables: RefundSummary;
  hash: string;
  termsAndConditions: TermsAndCondition[];
  fees: CarRateCodeFee[];
  paymentOptions: PaymentOption[];
  isAuthenticated: boolean;
  insuranceCoverage: InsuranceCoverage;
}

export const REFUND_POLICY = 'refund-policy';

export class StyledText {
  constructor(
    public text: string,
    public styles: string[]) {}
}

export interface TermsAndCondition {
  title: StyledText;
  paragraphs: StyledText[];
}

export interface PaymentOption {
    paymentOptionId: number;
    min: number;
    max: number;
    maxAllowable: number;
    step: number;
    excludedPaymentOptionIds: number[];
    alwaysAllowZeroSpend: boolean;
    forceMaxOrMin: boolean;
    usageFee: number;
    usageFeeLabel: string;
    refundable: boolean;
    optionalFees: OptionalPaymentFee[];
    memberBalance: number;
    applyToFees: boolean;
    applyToTaxes: boolean;
    applyToBaseRate: boolean;
    finalPointValue: number;
    isSelectedByDefault: boolean;
    defaultAmount: number;
    realMoneyValue: number;
    currencyType: string;
    currencySubType: string;
    currencyVendor: string;
    currencyLabel: string;
}

export interface Benefits {
  amount: number;
  description: string;
  type: BenefitType;
  units: string;
}

export interface OptionalPaymentFee {
    checkBoxLabel: string;
    feeLabel: string;
    feeAmount: number;
}

export enum BenefitType {
  dbcPercent = "dbcPercent",
  dbcApplied = "dbcApplied",
  dbcNewTotal = "dbcNewTotal",
  originalDailyRate = "originalDailyRate",
  newDailyRate = "newDailyRate",
  discountPercent = "discountPercent",
  discountApplied = "discountApplied",
  newTotal = "newTotal"
}

